import {styled} from '@mui/material'
import {Colors, Icons} from 'appearance'
import CopyAllIcon from '@mui/icons-material/CopyAll'
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined'
import Button from 'components/Control/Button'
import {
    useGenerateClientLinkMutation,
    useGetPlannerClientPdfsQuery,
    useI18n,
} from 'store/Store'
import {
    copyTextToClipboard,
    download,
    OpenFileInNewTab,
} from 'services/utils/misc'
import {useEffect, useState} from 'react'
import Snackbar from 'components/Display/Snackbar'
import {Spacer} from 'components/Page'
import MaxQuotaDialog from './MaxQuotaDialog'
import analyticsService from 'services/analytic'

const ClientRequests = ({client, isMobile, analyticsPressFunction}) => {
    const {i18n} = useI18n()
    const [snackbarText, setSnackbarText] = useState()
    const [sendSms, setSendSms] = useState()
    const [quotaError, setQoutaError] = useState()

    const pdfUrl = client?.PdfUrl
    const xlsxUrl = client?.XlsxUrl
    const {data: pdf} = useGetPlannerClientPdfsQuery({pdfUrl}, {skip: !pdfUrl})
    const {data: xlsx} = useGetPlannerClientPdfsQuery(
        {pdfUrl: xlsxUrl},
        {skip: !xlsxUrl}
    )
    const [
        doGenerateLink,
        {data: generateLinkResponse, error: generateLinkError},
    ] = useGenerateClientLinkMutation()

    const pdfFileName = pdfUrl?.split('/').pop()
    const xlsxFileName = xlsxUrl?.split('/').pop()

    const closeSnackbar = () => {
        setSnackbarText()
    }

    const generateRequestLink = (shouldSendSms) => {
        const {PlannerClientKey} = client
        if (PlannerClientKey === null) {
            setSnackbarText(i18n.t('clientRequests.requestLinkError'))
            return
        }
        setSendSms(shouldSendSms)
        doGenerateLink({PlannerClientKey, sendSms: !!shouldSendSms})
    }

    const copyRequestUrl = () => {
        analyticsPressFunction('copy_request')
        generateRequestLink(false)
    }

    const sendRequestSms = () => {
        analyticsPressFunction('send_request')
        generateRequestLink(true)
    }

    useEffect(() => {
        const snackbarText = sendSms
            ? i18n.t('clientRequests.sendRequestUrlSuccessMessage')
            : i18n.t('clientRequests.copyToClipboardMessage')

        if (generateLinkResponse) {
            if (generateLinkResponse?.data?.success === false) {
                setQoutaError(
                    generateLinkResponse?.data?.message ||
                        generateLinkResponse?.data?.msg
                )
                return
            }
            setSnackbarText(snackbarText)
            if (!sendSms) {
                copyTextToClipboard(generateLinkResponse.RequestUrl)
            }
        }
    }, [generateLinkResponse, i18n, sendSms, setQoutaError])

    useEffect(() => {
        if (generateLinkError) {
            setQoutaError(
                generateLinkError?.data?.message || generateLinkError?.data?.msg
            )
        }
    }, [generateLinkError, i18n, setQoutaError])

    return (
        <Container isMobile={isMobile}>
            <Column>
                <RequestButton
                    width={400}
                    startIcon={<CopyAllIcon />}
                    onClick={copyRequestUrl}>
                    {i18n.t('clients.copyNewRequest')}
                </RequestButton>
                <RequestButton
                    width={400}
                    startIcon={<SmsOutlinedIcon />}
                    onClick={sendRequestSms}>
                    {i18n.t('clients.sendRequestViaSMS')}
                </RequestButton>
            </Column>
            <Column>
                <RequestButton
                    startIcon={<Icons.Expand />}
                    disabled={!pdfUrl}
                    onClick={() => {
                        OpenFileInNewTab(pdf?.base64)
                        analyticsService.existingClientPressed('view_report')
                    }}>
                    {i18n.t('clients.viewLastReport')}
                </RequestButton>

                <RequestButton
                    disabled={!pdfUrl}
                    style={{
                        background: Colors.APP_WHITE,
                        padding: '6px 8px',
                    }}
                    variant="contained"
                    startIcon={<Icons.Pdf color={Colors.PDF_ICON} />}
                    onClick={() => {
                        download(pdf?.base64, pdfFileName)
                        analyticsService.existingClientPressed(
                            'download_report'
                        )
                    }}>
                    {i18n.t('clients.downloadLastReport')}
                </RequestButton>

                {xlsxUrl && (
                    <RequestButton
                        disabled={!xlsxUrl}
                        style={{
                            background: Colors.APP_WHITE,
                            padding: '6px 8px',
                        }}
                        variant="contained"
                        startIcon={
                            <Icons.Excel
                                sx={{fontSize: 10}}
                                width={25}
                                height={25}
                            />
                        }
                        onClick={() => {
                            download(xlsx?.base64, xlsxFileName)
                            analyticsService.existingClientPressed(
                                'download_report'
                            )
                        }}>
                        {i18n.t('clients.downloadXlsxReport')}
                    </RequestButton>
                )}
            </Column>
            {isMobile && <Spacer h={100} />}
            <Snackbar
                open={!!snackbarText}
                onClose={closeSnackbar}
                message={snackbarText}
            />
            <MaxQuotaDialog
                quotaError={quotaError}
                setQoutaError={setQoutaError}
            />
        </Container>
    )
}

export default ClientRequests

const Container = styled('div')`
    display: flex;
    flex-direction: ${({isMobile}) => (isMobile ? 'column' : 'row')};
    justify-content: space-between;
    width: 100%;
    padding-right: ${({isMobile}) => (isMobile ? 20 : 64)}px;
    padding-left: ${({isMobile}) => (isMobile ? 20 : 120)}px;
    margin-block: 24px;
`
const Column = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 20px;
`

const RequestButton = styled(Button)`
    font-size: 20px;
    color: ${Colors.PRIMARY_TEXT};
    display: flex;
    gap: 15px;
    justify-content: flex-start;
    & svg {
        width: 30px;
        height: 30px;
    }
    width: ${({width}) => width || 230}px;
`
